import { useReducer, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import {
	Stack as StackObject,
	LayoutTransform,
	SlipSheetOption,
	Pallet_t,
	SlipSheet_t,
	hasSlipSheet,
} from 'blackbox';
import {
	createDefaultSlipSheetConfig,
	inferAutoStackParamsFromStack,
} from './stackUtils';
import { StackMode, AutoStackParams } from './stackTypes';

interface UseStackEditorParams {
	allPallets: Pallet_t[];
	allSlipSheets: SlipSheet_t[];
}

interface StackEditorState {
	stack: StackObject;
	mode: StackMode;
	editingMode: 'new' | 'edit';
	startingAutoParams: AutoStackParams | null;
	palletIndex: number;
	stackIndex: number;
}

type Action =
	| { type: 'INIT_NEW'; palletIndex: number; nextStackName: string }
	| {
			type: 'INIT_EXISTING';
			palletIndex: number;
			stackIndex: number;
			stack: StackObject;
			autoParams: AutoStackParams | null;
	  }
	| { type: 'SET_MODE'; mode: StackMode }
	| { type: 'ADD_LAYER' }
	| { type: 'REMOVE_LAYER'; index: number }
	| { type: 'SET_LAYOUT'; index: number; layoutIndex: number }
	| {
			type: 'SET_TRANSFORM';
			index: number;
			transform: LayoutTransform.TransformType;
	  }
	| {
			type: 'SET_SLIPSHEET_PRESENT';
			index: number;
			present: boolean;
			defaultSlipSheetId: string;
	  }
	| { type: 'SET_SLIPSHEET_TYPE'; index: number; slipSheetId: string }
	| { type: 'UPDATE_NAME'; name: string }
	| { type: 'UPDATE_FROM_AUTO_PARAMS'; params: AutoStackParams };

function reducer(state: StackEditorState, action: Action): StackEditorState {
	const stackCopy: StackObject = {
		...state.stack,
		layers: [...state.stack.layers],
		slipSheet: {
			config: state.stack.slipSheet?.config ?? SlipSheetOption.None,
			ids: { ...state.stack.slipSheet?.ids },
			exclude: { ...state.stack.slipSheet?.exclude },
		},
	};

	switch (action.type) {
		case 'INIT_NEW': {
			return {
				stack: {
					name: action.nextStackName,
					id: uuidv4(),
					layers: [],
					slipSheet: createDefaultSlipSheetConfig(),
				},
				editingMode: 'new',
				mode: StackMode.Auto,
				startingAutoParams: null,
				palletIndex: action.palletIndex,
				stackIndex: -1,
			};
		}
		case 'INIT_EXISTING': {
			return {
				...state,
				stack: { ...action.stack },
				editingMode: 'edit',
				mode: action.autoParams ? StackMode.Auto : StackMode.Manual,
				startingAutoParams: action.autoParams,
				palletIndex: action.palletIndex,
				stackIndex: action.stackIndex,
			};
		}
		case 'SET_MODE': {
			return { ...state, mode: action.mode };
		}
		case 'ADD_LAYER': {
			stackCopy.layers.push({
				index: 0,
				layout_id: '',
				transform: LayoutTransform.TransformType.Identity,
			});
			stackCopy.slipSheet!.exclude![stackCopy.layers.length + 1] = true;
			return { ...state, stack: stackCopy };
		}
		case 'REMOVE_LAYER': {
			stackCopy.layers.splice(action.index, 1);

			// Reindex slipSheet.exclude and slipSheet.ids to match new layer indices
			const newExclude: Record<number, boolean> = {};
			const newIds: Record<number, string> = {};
            const adjustedAction = action.index + 1;
			Object.entries(stackCopy.slipSheet!.exclude ?? {}).forEach(
				([key, value]) => {
					const idx = parseInt(key, 10);
					if (idx < adjustedAction) newExclude[idx] = value;
					else if (idx > adjustedAction) newExclude[idx - 1] = value;
				}
			);

			Object.entries(stackCopy.slipSheet!.ids ?? {}).forEach(([key, value]) => {
				const idx = parseInt(key, 10);
				if (idx < adjustedAction) newIds[idx] = value;
				else if (idx > adjustedAction) newIds[idx - 1] = value;
			});

			stackCopy.slipSheet!.exclude = newExclude;
			stackCopy.slipSheet!.ids = newIds;

			return { ...state, stack: stackCopy };
		}

		case 'SET_LAYOUT': {
			stackCopy.layers[action.index].index = action.layoutIndex;
			return { ...state, stack: stackCopy };
		}
		case 'SET_TRANSFORM': {
			stackCopy.layers[action.index].transform = action.transform;
			return { ...state, stack: stackCopy };
		}
		case 'SET_SLIPSHEET_PRESENT': {
			if (stackCopy.slipSheet) {
				stackCopy.slipSheet.config = SlipSheetOption.Custom;
				stackCopy.slipSheet.exclude![action.index] = !action.present;
				if (!action.present) delete stackCopy.slipSheet.ids[action.index];
				else stackCopy.slipSheet.ids[action.index] = action.defaultSlipSheetId;
			}
			return { ...state, stack: stackCopy };
		}
		case 'SET_SLIPSHEET_TYPE': {
			stackCopy.slipSheet!.ids[action.index] = action.slipSheetId;
			return { ...state, stack: stackCopy };
		}
		case 'UPDATE_NAME': {
			stackCopy.name = action.name;
			return { ...state, stack: stackCopy };
		}
		case 'UPDATE_FROM_AUTO_PARAMS': {
			stackCopy.layers = [];
			for (let i = 0; i < action.params.layerCount; i++) {
				stackCopy.layers.push({
					index: action.params.layoutIndex,
					layout_id: '',
					transform:
						(i + 1) % 2 === 0
							? action.params.evenTransform
							: action.params.oddTransform,
				});
			}
			stackCopy.slipSheet =
				action.params.slipSheet?.config === SlipSheetOption.Custom
					? action.params.slipSheet
					: { ...action.params.slipSheet, exclude: {} };

			const normalizedExclude: Record<number, boolean> = {};

			for (let i = 0; i < stackCopy.layers.length + 1; i++) {
				normalizedExclude[i] = !hasSlipSheet(
					i,
					stackCopy.layers.length,
					stackCopy.slipSheet
				);
			}
			stackCopy.slipSheet.exclude = normalizedExclude;

			return {
				...state,
				stack: stackCopy,
				startingAutoParams: action.params,
			};
		}
		default:
			return state;
	}
}

export function useStackEditor({
	allPallets,
	allSlipSheets,
}: UseStackEditorParams) {
	const [state, dispatch] = useReducer(reducer, {
		stack: {
			id: uuidv4(),
			name: '',
			layers: [],
			slipSheet: createDefaultSlipSheetConfig(),
		},
		editingMode: 'new',
		mode: StackMode.Auto,
		startingAutoParams: null,
		palletIndex: -1,
		stackIndex: -1,
	});

	const startEdit = useCallback(
		(palletIndex: number, stackIndex: number) => {
			if (stackIndex < 0) {
				const stackCount = allPallets[palletIndex]?.Stack.length || 0;
				dispatch({
					type: 'INIT_NEW',
					palletIndex,
					nextStackName: `Stack ${stackCount + 1}`,
				});
			} else {
				const stack = allPallets[palletIndex].Stack[stackIndex];
				const autoParams = inferAutoStackParamsFromStack(
					allPallets,
					palletIndex,
					stackIndex
				);
				dispatch({
					type: 'INIT_EXISTING',
					palletIndex,
					stackIndex,
					stack,
					autoParams,
				});
			}
		},
		[allPallets]
	);

	return {
		...state,
		startEdit,
		addLayer: () => dispatch({ type: 'ADD_LAYER' }),
		removeLayer: (index: number) => dispatch({ type: 'REMOVE_LAYER', index }),
		setLayout: (index: number, layoutIndex: number) =>
			dispatch({ type: 'SET_LAYOUT', index, layoutIndex }),
		setTransform: (index: number, transform: LayoutTransform.TransformType) =>
			dispatch({ type: 'SET_TRANSFORM', index, transform }),
		setSlipSheetPresence: (index: number, present: boolean) =>
			dispatch({
				type: 'SET_SLIPSHEET_PRESENT',
				index,
				present,
				defaultSlipSheetId: allSlipSheets[0]?.id || '',
			}),
		setSlipSheetType: (index: number, slipSheetId: string) =>
			dispatch({ type: 'SET_SLIPSHEET_TYPE', index, slipSheetId }),
		updateName: (name: string) => dispatch({ type: 'UPDATE_NAME', name }),
		updateFromAutoParams: (params: AutoStackParams) =>
			dispatch({ type: 'UPDATE_FROM_AUTO_PARAMS', params }),
		setMode: (mode: StackMode) => dispatch({ type: 'SET_MODE', mode }),
	};
}
